import { config } from '../../config';
import { canAuthorSourceless } from '../distillation';
import { MATCH_STATE } from '../machine';
import { ok, reject } from '../result';
import { ARTIFACT_PHASE } from '../../types';
import type { Decision, DistillationOp, PromoteTalkPointsCommand, PromoteTalkPointsSnapshot } from './types';

/**
 * DIST-2 / DIST-3 / DIST-6 / DIST-7 — **the distillation write** (build unit U-14b).
 *
 * One handler, one op, one table. `Decision<DistillationOp>` reaches `talkPoints` and nothing else,
 * so *"distillation alters no hypothesis"* — no count, no status, no `boardPosition` — is a compile
 * error rather than a review finding, exactly as HT-6 is for `handlePromoteHalftimeAdjustment`.
 *
 * ── The rank rule is NOT here, and that is deliberate ───────────────────────
 *
 * §10.2: *"`TalkPoint.rank` is derived from selection order, never incremented."* This handler emits
 * the ordered list unchanged and `repos/talkPoints.writeSelection` deletes-then-rewrites `1..n`
 * inside the transaction. There is no arithmetic on a rank anywhere on this path, in this file or in
 * its caller, which is what makes a gap **unrepresentable** rather than merely avoided — and a gap
 * is what would leave `CompleteDistillation`'s postcondition aborting with the match stuck in
 * `distill` and no explanation the user could act on.
 */

const MODULE = 'commands/distill';

/**
 * DIST-2 — rewrite the `phase=fullTime` selection.
 *
 * ── The state gate is `distill` exactly ─────────────────────────────────────
 *
 * Not `state === fullTime || triage || distill`. DIST-1 requires triage to be *"passed, possibly
 * empty, before distillation"*, and SES-1 places `PassTriage` between them — so a full-time talk
 * point written from `fullTime` or `triage` would be the match's output authored before the step
 * that exists to make sure nothing is still unreviewed. `archived` is out from the other side: INV-6
 * has already been asserted by then, and a write after it would leave a match whose stored talk
 * points no longer satisfy the invariant its own archive claims.
 *
 * §2.2 supplies the way back — `Defer` returns `distill → fullTime` — so the window closing is never
 * a trap.
 *
 * ── What is checked here and what is checked elsewhere ──────────────────────
 *
 * | Rule | Here | Also |
 * |---|---|---|
 * | INV-6's **upper** bound | typed rejection the UI renders | `writeSelection` aborts the transaction |
 * | INV-6's **exactly** | **no** — see below | `machine.ts`'s `ERR_TALKPOINT_SET` guard **and** the `talkPointSet` postcondition |
 * | INV-13 / DIST-3 instruction text | typed rejection | `assertInv13` aborts; the union makes it a compile error in-tree |
 * | DIST-6 dangling source | typed rejection | — |
 * | DIST-7's permission | typed rejection | the surface renders the affordance inert |
 *
 * **DIST-2's *exactly* is deliberately absent from this handler.** The selection is built one
 * promotion at a time, so a 1- and a 2-entry selection are normal intermediate states; asserting
 * *exactly three* on every write would make the first promotion unwritable. The `exactly` belongs to
 * the transition that consumes the set, and it is enforced there twice — as a guard that rejects
 * renderably and as an in-transaction postcondition that aborts (§10.2: *"INV-6 is not a UI rule"*).
 */
export function handlePromoteTalkPoints(
  cmd: PromoteTalkPointsCommand,
  snap: PromoteTalkPointsSnapshot,
): Decision<DistillationOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (match.state !== MATCH_STATE.distill) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_NOT_AT_DISTILLATION',
      'Talk points can only be chosen at distillation.',
      { state: match.state },
    );
  }

  if (cmd.ordered.length > config.TALKPOINT_COUNT) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_TALKPOINT_LIMIT',
      `A match carries ${String(config.TALKPOINT_COUNT)} talk points. Drop one to add another.`,
      { count: cmd.ordered.length },
    );
  }

  const known = new Set(snap.hypothesisIds);
  const evidenced = new Set(snap.evidencedHypothesisIds);
  // DIST-7's gate, **re-based by DIST-9** (revision 4) and answered from the same read DIST-6's
  // uses. It counted `hypothesisIds.length` — the requirement's own phrase at the time, "how
  // many hypotheses exist for a match" — which PRE-9 made ~49 on every match ever created, so
  // the hatch was permanently shut. Read inside the transaction, so an increment committed on
  // another surface between render and tap is counted.
  const sourcelessAllowed = canAuthorSourceless(evidenced.size);

  for (const entry of cmd.ordered) {
    if (entry.matchId !== cmd.matchId || entry.phase !== ARTIFACT_PHASE.fullTime) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_COMMAND_ROW_MISMATCH',
        'That talk point does not belong to this match’s distillation.',
        { id: entry.id, phase: entry.phase },
      );
    }

    // INV-13 / DIST-3 — *"each `phase=fullTime` TalkPoint requires instruction-phrased free text."*
    // The union already makes a missing field a compile error in this tree; this is the renderable
    // half, for a value that crossed the UI boundary. `assertInv13` inside the transaction is the
    // third layer and the one that also covers an import (§11.9).
    if (entry.instructionText.trim() === '') {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_INSTRUCTION_REQUIRED',
        'A talk point needs the instruction you would give. A blank line is not a talk point.',
        { id: entry.id },
      );
    }

    if (entry.sourceHypothesisId === null) {
      if (!sourcelessAllowed) {
        return reject(
          MODULE,
          cmd.matchId,
          'ERR_SOURCE_REQUIRED',
          `This match saw ${String(evidenced.size)} patterns — choose one of them instead of writing a talk point from nothing.`,
          { id: entry.id, evidencedCount: evidenced.size, hypothesisCount: known.size },
        );
      }
    } else if (!known.has(entry.sourceHypothesisId)) {
      // DIST-6 — `sourceHypothesisId` is what FILE-1 reads to show which tracked patterns became
      // instructions. A dangling id is a blank line on the share card with nothing reporting why.
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_HYPOTHESIS_NOT_FOUND',
        'That pattern is no longer on this match.',
        { id: entry.id, sourceHypothesisId: entry.sourceHypothesisId },
      );
    } else if (!evidenced.has(entry.sourceHypothesisId)) {
      /**
       * DIST-9 (revision 4) — *"a hypothesis with `cachedCount = 0` and `statusOverride === null`
       * is **not** offered for promotion to a `phase=fullTime` TalkPoint."*
       *
       * **Enforced here and not only on the surface**, for design §4 / §19 F-30's reason — *"an
       * invariant enforced only by a UI is not an invariant"* — and for DIST-7's precedent one
       * branch up, whose permission half is refused here too. Under PRE-9 the excluded set is
       * ~43 rows on a normal match, so a stale surface, a replayed command or an import is a
       * live path to a share card carrying an instruction about a pattern nobody observed, which
       * is P-3's entire output built on something that did not happen.
       *
       * A distinct code from DIST-6's above, deliberately: *"no longer on this match"* would be
       * **false** here and would send a reader looking for a data fault. The user's route to
       * saying this anyway is DIST-7's source-less talk point, which is where an unevidenced
       * instruction honestly belongs.
       */
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_HYPOTHESIS_UNEVIDENCED',
        'This match recorded nothing for that pattern, so it is not one of its findings. Write it as a talk point of its own instead.',
        { id: entry.id, sourceHypothesisId: entry.sourceHypothesisId },
      );
    }
  }

  const ids = cmd.ordered.map((entry) => entry.id);
  if (new Set(ids).size !== ids.length) {
    return reject(MODULE, cmd.matchId, 'ERR_TALKPOINT_DUPLICATE', 'The same talk point was sent twice.', {
      ids,
    });
  }

  // A second duplicate test, on a different key, refusing a different outcome: the same **pattern**
  // promoted twice under two ids. `&[matchId+phase+rank]` would accept it, so nothing downstream
  // objects — and CARD-1 would render one hypothesis as two of the three things the user says at
  // full time. `null` is exempt: DIST-7's source-less points are legitimately indistinguishable by
  // source, and it is their instruction text that tells them apart.
  const sources = cmd.ordered
    .map((entry) => entry.sourceHypothesisId)
    .filter((id): id is string => id !== null);
  if (new Set(sources).size !== sources.length) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_TALKPOINT_DUPLICATE',
      'That pattern is already one of your talk points.',
      { sources },
    );
  }

  return ok({
    ops: [
      {
        t: 'writeTalkPointSelection',
        matchId: cmd.matchId,
        phase: ARTIFACT_PHASE.fullTime,
        ordered: cmd.ordered,
      },
    ],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'DIST-2: full-time talk-point selection written',
        data: {
          count: cmd.ordered.length,
          was: snap.existingFullTime.length,
          sourceless: cmd.ordered.length - sources.length,
        },
      },
    ],
    // No postcondition. `talkPointSet` asserts INV-6's *exactly*-`TALKPOINT_COUNT` rule, which binds
    // at `CompleteDistillation` and not at every promotion — running it here would abort the first
    // and second promotions of every match.
    post: [],
  });
}
